import type { SidebarMenuItem, User, UserResponse } from '@apparatix/types';
import { AppStore, registerMenuItemActivationHandler } from '@apparatix/stores/AppStore';
import { useBackendDataConverter } from './useBackendDataConverter';
import { useFetch } from './useFetch';
import { useStateSync } from './useStateSync';

type MenuShortcutsPayload = {
  favoriteMenuItemIds: number[];
  recentMenuItemIds: number[];
};

const FOCUS_REFRESH_INTERVAL = 30_000;
const refreshState = {
  lastRefreshAt: 0,
  isRefreshing: false,
  refreshPending: false,
};

export const useMenuNavigation = () => {
  const { navigationObjectToTopLevelNavigation } = useBackendDataConverter();
  const {
    destroy, get, post, put,
  } = useFetch();

  const setMenuShortcuts = (payload: MenuShortcutsPayload): void => {
    AppStore.setMenuShortcuts(payload.favoriteMenuItemIds, payload.recentMenuItemIds);
    AppStore.currentUser.favoriteMenuItemIds = [...payload.favoriteMenuItemIds];
    AppStore.currentUser.recentMenuItemIds = [...payload.recentMenuItemIds];
  };

  const applyUserMenu = (user: User): void => {
    setMenuShortcuts(user);
    AppStore.reconcileNavigation(navigationObjectToTopLevelNavigation(user.mainMenuItems));
    AppStore.currentUser.mainMenuItems = user.mainMenuItems;
  };

  const refreshMenu = async (): Promise<void> => {
    if (refreshState.isRefreshing) {
      refreshState.refreshPending = true;

      return;
    }

    refreshState.isRefreshing = true;

    do {
      refreshState.refreshPending = false;

      try {
        const response = await get<UserResponse>('/user/current');
        applyUserMenu(response.data);
        refreshState.lastRefreshAt = Date.now();
      } catch {
        refreshState.lastRefreshAt = 0;
      }
    } while (refreshState.refreshPending);

    refreshState.isRefreshing = false;
  };

  const refreshMenuOnFocus = (): void => {
    if (Date.now() - refreshState.lastRefreshAt < FOCUS_REFRESH_INTERVAL) {
      return;
    }

    refreshMenu();
  };

  const recordAccess = (item: SidebarMenuItem): void => {
    const menuSystemId = item.systemId;
    if (item.isHomePage || menuSystemId < 1 || AppStore.isImpersonating) {
      return;
    }

    const previousFavorites = [...AppStore.favoriteMenuItemIds];
    const previousRecents = [...AppStore.recentMenuItemIds];

    if (!AppStore.favoriteMenuItemIds.includes(menuSystemId)) {
      AppStore.setMenuShortcuts(
        AppStore.favoriteMenuItemIds,
        [menuSystemId, ...AppStore.recentMenuItemIds.filter(id => id !== menuSystemId)].slice(0, 10),
      );
    }

    post<MenuShortcutsPayload>(`/user/menu-recents/${menuSystemId}`, {})
      .then(response => response
        ? setMenuShortcuts(response)
        : AppStore.setMenuShortcuts(previousFavorites, previousRecents))
      .catch(() => AppStore.setMenuShortcuts(previousFavorites, previousRecents));
  };

  const activateMenuItem = (item: SidebarMenuItem): boolean => AppStore.navigateToMenuItem(item.systemId, item.parentNavigationKey);

  const registerMenuAccessTracking = (): (() => void) => registerMenuItemActivationHandler(recordAccess);

  const activateDefaultMenuAndRegisterAccessTracking = (): (() => void) => {
    if (AppStore.defaultPage) {
      AppStore.navigationItems.find(page => page.label === AppStore.defaultPage)?.command?.();
    }

    return registerMenuAccessTracking();
  };

  const toggleFavorite = async (item: SidebarMenuItem): Promise<boolean> => {
    if (item.systemId < 1 || AppStore.isImpersonating) {
      return false;
    }

    const previousFavorites = [...AppStore.favoriteMenuItemIds];
    const previousRecents = [...AppStore.recentMenuItemIds];
    const isFavorite = previousFavorites.includes(item.systemId);

    if (item.isHomePage && !isFavorite) {
      return false;
    }

    const nextFavorites = isFavorite
      ? previousFavorites.filter(id => id !== item.systemId)
      : [...previousFavorites, item.systemId];

    AppStore.setMenuShortcuts(nextFavorites, previousRecents);

    const response = isFavorite
      ? await destroy<MenuShortcutsPayload>(`/user/menu-favorites/${item.systemId}`)
      : await post<MenuShortcutsPayload>(`/user/menu-favorites/${item.systemId}`, {});

    if (!response) {
      AppStore.setMenuShortcuts(previousFavorites, previousRecents);

      return false;
    }

    setMenuShortcuts(response);

    return true;
  };

  const reorderFavorites = async (menuItemIds: number[]): Promise<boolean> => {
    if (AppStore.isImpersonating) {
      return false;
    }

    const previousFavorites = [...AppStore.favoriteMenuItemIds];
    AppStore.setMenuShortcuts(menuItemIds, AppStore.recentMenuItemIds);

    const response = await put<MenuShortcutsPayload>('/user/menu-favorites', { menuItemIds });
    if (!response) {
      AppStore.setMenuShortcuts(previousFavorites, AppStore.recentMenuItemIds);

      return false;
    }

    setMenuShortcuts(response);

    return true;
  };

  const registerMenuSync = (): void => {
    const { subscribe } = useStateSync(AppStore.tenantId, AppStore.currentUser.id);
    subscribe<{ refreshMenu: boolean }>('MenuAssignmentsChanged', refreshMenu);
    subscribe<MenuShortcutsPayload>('MenuShortcutsChanged', setMenuShortcuts);
  };

  return {
    activateMenuItem,
    activateDefaultMenuAndRegisterAccessTracking,
    applyUserMenu,
    refreshMenu,
    refreshMenuOnFocus,
    registerMenuAccessTracking,
    registerMenuSync,
    reorderFavorites,
    setMenuShortcuts,
    toggleFavorite,
  };
};
